import { useCallback, useRef } from 'react';

import { useMutation } from '@apollo/client';

import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import type { ProfileSetting } from 'bundles/account-settings/constants/SettingsConstants';
import UpdateFullNameMutation from 'bundles/account-settings/queries/UpdateFullNameMutation.graphql';
import UpdateLocaleMutation from 'bundles/account-settings/queries/UpdateLocaleMutation.graphql';
import UpdateTimezoneMutation from 'bundles/account-settings/queries/UpdateTimezoneMutation.graphql';
import type {
  UpdateFullNameMutationMutation,
  UpdateFullNameMutationMutationVariables,
} from 'bundles/account-settings/queries/__generated__/UpdateFullNameMutation';
import type {
  UpdateLocaleMutationMutation,
  UpdateLocaleMutationMutationVariables,
} from 'bundles/account-settings/queries/__generated__/UpdateLocaleMutation';
import type {
  UpdateTimezoneMutationMutation,
  UpdateTimezoneMutationMutationVariables,
} from 'bundles/account-settings/queries/__generated__/UpdateTimezoneMutation';

const { Fields, Sections } = SettingsConstants;

const GATEWAY_CONTEXT = { context: { clientName: 'gatewayGql' } };

export const useUpdateBasicInformation = () => {
  const [updateFullName] = useMutation<UpdateFullNameMutationMutation, UpdateFullNameMutationMutationVariables>(
    UpdateFullNameMutation,
    GATEWAY_CONTEXT
  );
  const [updateTimezone] = useMutation<UpdateTimezoneMutationMutation, UpdateTimezoneMutationMutationVariables>(
    UpdateTimezoneMutation,
    GATEWAY_CONTEXT
  );
  const [updateLocale] = useMutation<UpdateLocaleMutationMutation, UpdateLocaleMutationMutationVariables>(
    UpdateLocaleMutation,
    GATEWAY_CONTEXT
  );

  // Guards against overlapping calls (e.g. a caller invoking this before a prior
  // call resolves) racing the same way concurrent mutations within one call would.
  const isUpdatingRef = useRef(false);

  return useCallback(
    async (settings: ProfileSetting[]) => {
      if (settings.length === 0 || isUpdatingRef.current) {
        return;
      }

      // Second line of defence for LO-5088: every locale/timezone mutation in the app funnels
      // through here, and `setting.value ?? ''` below would otherwise turn an absent value into a
      // blank one that learner-object rejects. Drop those settings before they become mutations.
      const safeSettings = settings.filter(
        (setting) =>
          (setting.type !== Fields.LOCALE && setting.type !== Fields.TIMEZONE) || (setting.value ?? '').trim() !== ''
      );
      if (safeSettings.length === 0) {
        return;
      }

      isUpdatingRef.current = true;
      SettingsServerActionCreators.updateStarted(Sections.BASIC_INFORMATION);

      try {
        // Mutations run sequentially: each one does a read-modify-write of the whole
        // learner object on the backend, so firing them concurrently races and the
        // last write to land silently reverts the others' changes.
        const outcomes: Array<{ field: 'fullName' | 'other'; hasError: boolean }> = [];
        for (const setting of safeSettings) {
          switch (setting.type) {
            case Fields.FULL_NAME: {
              const result = await updateFullName({ variables: { input: { fullName: setting.value ?? '' } } });
              outcomes.push({
                field: 'fullName',
                hasError:
                  result.data?.LearnerObject_UpdateFullName?.__typename === 'LearnerObject_UpdateFullNameResultError',
              });
              break;
            }
            case Fields.TIMEZONE: {
              const result = await updateTimezone({ variables: { input: { timezone: setting.value ?? '' } } });
              outcomes.push({
                field: 'other',
                hasError:
                  result.data?.LearnerObject_UpdateTimezone?.__typename === 'LearnerObject_UpdateTimezoneResultError',
              });
              break;
            }
            case Fields.LOCALE: {
              const result = await updateLocale({ variables: { input: { locale: setting.value ?? '' } } });
              outcomes.push({
                field: 'other',
                hasError:
                  result.data?.LearnerObject_UpdateLocale?.__typename === 'LearnerObject_UpdateLocaleResultError',
              });
              break;
            }
            default:
              throw new Error(`Unsupported basic information field: ${setting.type}`);
          }
        }

        const hasFullNameError = outcomes.some((outcome) => outcome.field === 'fullName' && outcome.hasError);
        const hasOtherError = outcomes.some((outcome) => outcome.field === 'other' && outcome.hasError);

        if (hasFullNameError) {
          SettingsServerActionCreators.updateFailedNameMalformed(Sections.BASIC_INFORMATION);
        } else if (hasOtherError) {
          SettingsServerActionCreators.updateFailed(Sections.BASIC_INFORMATION);
        } else {
          SettingsServerActionCreators.updatedBasicInformation(safeSettings);
          SettingsServerActionCreators.updateSucceeded(Sections.BASIC_INFORMATION, undefined);
        }
      } catch {
        SettingsServerActionCreators.updateFailed(Sections.BASIC_INFORMATION);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [updateFullName, updateTimezone, updateLocale]
  );
};

export default useUpdateBasicInformation;
